import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '../lib/api'
import type { ClubProfileVisibility } from '../lib/types'

/**
 * DECLARED HERE RATHER THAN IN `lib/types.ts`, exactly as `useRoundHistory`
 * and `useOutings` declare theirs. That file is copied VERBATIM into the
 * Caddie app by `bin/mobile contract`, so a shape put there lands in the app's
 * source tree whether the app wants it or not. This payload is the web club's
 * profile page and nothing else reads it; `ClubProfileVisibility` stays in
 * `lib/types.ts` because it types a field of `/me`, which the app really does
 * read.
 */
/**
 * One player on a round shown on somebody's profile.
 *
 * `user_id` and `display_name` are WITHHELD TOGETHER when the viewer is not
 * entitled to that person's identity: the name reads "A GTG GC member" (or
 * "A guest", or "A former member" for an account since deleted) and the id is
 * null. A client must therefore treat `user_id: null` as "not linkable",
 * never as "guest" — `guest` is the field that answers that.
 *
 * There is no `email` field, at any visibility, for any viewer.
 */
export interface MemberProfilePlayer {
  user_id: string | null
  display_name: string
  /** True when `display_name` is the player's own name rather than a placeholder. */
  named: boolean
  guest: boolean
  total_strokes: number | null
  score_differential: number | null
}

/**
 * One round in a member's history. `scores` is the PROFILE OWNER's card, hole
 * by hole — never another player's, whose numbers appear only as the totals on
 * `players`. Empty rather than absent when no cells were entered.
 */
/**
 * One hole of the profile owner's card (mirrors
 * GolfScoringSerializers#hole_score_json). `putt_distances` is METRES, putt 1
 * first, and may be shorter than `putts` — marking a putt is optional detail,
 * not a truncated payload.
 */
export interface MemberProfileHoleScore {
  hole_number: number
  strokes: number | null
  putts: number | null
  fairway_hit: boolean | null
  putt_distances: number[] | null
}

export interface MemberProfileRound {
  id: string
  course_key: string
  course_name: string
  played_on: string | null
  posted_at: string | null
  holes_count: number
  tee_name: string | null
  total_strokes: number | null
  course_handicap: number | null
  score_differential: number | null
  scores: MemberProfileHoleScore[]
  players: MemberProfilePlayer[]
}

/**
 * A member's profile.
 *
 * `member_number` and `tier` are null unless the viewer is a member in good
 * standing — the club publishes its roll to its members, and a member setting
 * their profile public is publishing their golf, not the register.
 * `visibility` is present ONLY on your own profile: it is the setting, and
 * only one person may change it (PATCH /api/v1/me).
 */
export interface MemberProfile {
  user_id: string
  display_name: string
  club_mark: ClubMark
  handicap_index: number | null
  handicap_status: 'rated' | 'unrated'
  member_number: number | null
  tier: string | null
  visibility?: ClubProfileVisibility | null
  rounds_count: number
  rounds: MemberProfileRound[]
}

export interface MemberProfileResponse {
  profile: MemberProfile
}

/**
 * One member's profile (`GET /api/v1/golf/members/:id/profile`).
 *
 * `absent` is a first-class state rather than an error string, because the
 * server answers a hidden profile and a member who never existed with exactly
 * the same 404 — deliberately, so the API never confirms an account to
 * somebody the member decided cannot see them
 * (docs/fireside/member-profiles.md). The screen therefore has one thing to
 * render for both, and MUST NOT say "this member is private": the client does
 * not know that, and guessing would leak precisely what the 404 protects.
 */
export function useMemberProfile(userId: string) {
  const [profile, setProfile] = useState<MemberProfile | null>(null)
  const [absent, setAbsent] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(() => {
    return api
      .get<MemberProfileResponse>(`/golf/members/${encodeURIComponent(userId)}/profile`)
      .then((data) => {
        setProfile(data.profile)
        setAbsent(false)
        setError(null)
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 404) {
          setAbsent(true)
          setProfile(null)
          setError(null)
          return
        }
        setError(e instanceof Error ? e.message : 'We could not open that profile.')
      })
      .finally(() => setIsLoading(false))
  }, [userId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { profile, absent, isLoading, error, refresh }
}
